/**
 * @file useTransferStockOptions.ts
 * @description Справочники формы строки перемещения (phase19.23 этап B):
 * остатки склада-источника, его ячейки и список складов назначения.
 *
 * Фасовку выбираем не по каталогу, а по ОСТАТКАМ склада-источника
 * (`GET /admin/wms/stock`): увезти можно только то, что там лежит, и оттуда же
 * приходят партии с их текущими ячейками — «Откуда» в строке считается без
 * второго запроса.
 *
 * После добавления строки остатки перечитываем (`reload`): часть партии могла
 * уехать, и держать устаревший список в state значило бы спорить с сервером.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { binsApi, type StorageBinDto } from "../../../lib/api-bins";
import {
  formatSkuName,
  stockApi,
  warehousesApi,
  type StockLevelDto,
  type WarehouseDto,
} from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Партия на складе-источнике: сколько лежит и где стоит сейчас. */
export interface TransferStockLot {
  lotId: string;
  batchNumber: string;
  expiresAt: string | null;
  quantity: number;
  reserved: number;
  binId: string | null;
  binCode: string | null;
}

/** Фасовка с остатком — первый шаг формы строки. */
export interface TransferStockSku {
  skuId: string;
  name: string;
  code: string;
  totalQuantity: number;
  lots: TransferStockLot[];
}

/** Подпись партии в списке выбора: срок, адрес и остаток одной строкой. */
export function formatTransferLot(lot: TransferStockLot): string {
  return [
    lot.expiresAt
      ? `до ${new Date(lot.expiresAt).toLocaleDateString("ru-RU")}`
      : "без срока",
    lot.binCode ?? "без ячейки",
    `${lot.quantity} шт`,
  ].join(" · ");
}

/** Остаток без партии переставить нельзя: адрес хранения живёт на партии. */
function toLot(row: StockLevelDto): TransferStockLot | null {
  if (!row.lot) return null;
  return {
    lotId: row.lot.id,
    batchNumber: row.lot.batchNumber,
    expiresAt: row.lot.expiresAt,
    quantity: Number(row.quantity),
    reserved: Number(row.totalReserved),
    binId: row.lot.bin?.id ?? null,
    binCode: row.lot.bin?.code ?? null,
  };
}

export function useTransferStockOptions(fromWarehouseId: string) {
  const [rows, setRows] = useState<StockLevelDto[]>([]);
  const [bins, setBins] = useState<StorageBinDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Документ ещё не загружен — грузить нечего, но и «вечную загрузку»
    // показывать нельзя: форма строки просто ждёт склад-источник.
    if (!fromWarehouseId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      stockApi.list({ warehouseId: fromWarehouseId, onlyPositive: true }),
      // Диалогу выбора сводка содержимого не нужна — экономим запрос.
      binsApi.list({ warehouseId: fromWarehouseId, withSummary: false }),
      warehousesApi.list(),
    ])
      .then(([stock, binList, warehouseList]) => {
        if (cancelled) return;
        // Реестр остатков отдаёт строки вместе с признаком «показано не всё»
        // (`truncated`); подбору строки перемещения нужен только сам список.
        setRows(stock.items);
        setBins(binList);
        setWarehouses(warehouseList);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить остатки склада"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fromWarehouseId, reloadKey]);

  /** Остатки склада, свёрнутые в фасовки с партиями. */
  const skus = useMemo<TransferStockSku[]>(() => {
    const map = new Map<string, TransferStockSku>();
    for (const row of rows) {
      const lot = toLot(row);
      if (!lot) continue;
      const existing = map.get(row.skuId);
      if (existing) {
        existing.totalQuantity += lot.quantity;
        existing.lots.push(lot);
        continue;
      }
      map.set(row.skuId, {
        skuId: row.skuId,
        name: formatSkuName(row.sku),
        code: row.sku?.code ?? row.skuId,
        totalQuantity: lot.quantity,
        lots: [lot],
      });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [rows]);

  /** Склады назначения: сам источник в список не попадает. */
  const targetWarehouses = useMemo(
    () => warehouses.filter((w) => w.id !== fromWarehouseId && w.isActive),
    [warehouses, fromWarehouseId],
  );

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  return { skus, bins, warehouses, targetWarehouses, loading, error, reload };
}
